import { useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import {
  useMyResources,
  useSuggestResource,
  type ResourceVocabulary,
} from "@/features/resources/resources";
import { TagInput } from "@/components/TagInput";
import { FormSkeleton } from "@/components/Skeletons";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { sectionTitleClass } from "@/lib/typography";

/** A shelf of its own, not an escape hatch. Letting people invent categories
 *  is how six shelves become forty, and the six were built over six years. */
const OTHER = "Other";

/**
 * Adding to the library, beside My Profile and My Legacies.
 *
 * It is here rather than on the resources page for the same reason legacies
 * are: that page is for finding something, and a form for adding one sitting
 * on top of it is a button most people never want. The form is the old
 * suggest-a-resource dialog, unchanged in shape.
 */
export default function MyResourcesPage() {
  const { data, isPending } = useMyResources();
  const suggest = useSuggestResource();
  const [open, setOpen] = useState(false);

  if (isPending) return <FormSkeleton />;
  const vocabulary = data?.vocabulary ?? { categories: [], tags: [], locations: [] };

  return (
    <div className="max-w-3xl space-y-4 pb-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl">My resources</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Anything you add is reviewed before it joins the library.
          </p>
        </div>
        {!open && (
          <Button variant="outline" onClick={() => setOpen(true)}>
            <Plus data-slot="icon" /> Add a resource
          </Button>
        )}
      </div>

      {open && (
        <ResourceForm
          vocabulary={vocabulary}
          busy={suggest.isPending}
          onCancel={() => setOpen(false)}
          onSubmit={(payload) =>
            suggest.mutate(payload, {
              onSuccess: () => {
                setOpen(false);
                toast.success("Sent for review");
              },
              onError: (error) => toast.error(error.message),
            })
          }
        />
      )}

      {(data?.resources ?? []).length === 0 ? (
        <p className="text-muted-foreground text-sm">
          You have not added anything yet.
        </p>
      ) : (
        <div className="space-y-3">
          {(data?.resources ?? []).map((r) => (
            <Card key={r.id}>
              <CardHeader>
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-base">{r.title}</CardTitle>
                  <Badge variant="secondary">{r.category}</Badge>
                  {r.status === "pending" && (
                    <Badge variant="outline">Waiting for review</Badge>
                  )}
                  {r.status === "declined" && (
                    <span className="text-xs font-medium text-amber-600 dark:text-amber-500">
                      Not accepted
                    </span>
                  )}
                </div>
                {r.description && (
                  <CardDescription>{r.description}</CardDescription>
                )}
                {r.tags.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {r.tags.map((t) => (
                      <Badge key={t} variant="outline" className="text-xs">
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardHeader>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function ResourceForm({
  vocabulary,
  busy,
  onCancel,
  onSubmit,
}: {
  vocabulary: ResourceVocabulary;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (payload: {
    title: string;
    description: string;
    url: string;
    category: string;
    tags: string[];
    location: string;
    email: string;
    phone: string;
  }) => void;
}) {
  const [form, setForm] = useState({
    title: "",
    description: "",
    url: "",
    location: "",
    email: "",
    phone: "",
  });
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState<string[]>([]);

  const chosenCategory = category;
  // A title and a shelf. Nothing else: an entry can be a paragraph somebody
  // wrote, which is a resource in the ordinary sense of the word even with
  // nothing to click.
  const ready = Boolean(form.title.trim() && chosenCategory);

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: e.target.value });

  return (
    <Card>
      <CardHeader>
        <CardTitle className={sectionTitleClass}>Add a resource</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="r-title">Name</Label>
          <Input id="r-title" value={form.title} onChange={set("title")} />
        </div>

        <div className="space-y-2">
          <Label htmlFor="r-category">Category</Label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger id="r-category" className="w-full">
              <SelectValue placeholder="Choose a category" />
            </SelectTrigger>
            <SelectContent>
              {vocabulary.categories.map((c) => (
                <SelectItem key={c} value={c}>
                  {c}
                </SelectItem>
              ))}
              {!vocabulary.categories.includes(OTHER) && (
                <SelectItem value={OTHER}>{OTHER}</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Tags</Label>
          <TagInput value={tags} onChange={setTags} suggestions={vocabulary.tags} />
          <p className="text-muted-foreground text-xs">
            What they do, or what kind of thing it is. Existing tags are
            suggested as you type. Picking one keeps the library together.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="r-description">Description</Label>
          <Textarea
            id="r-description"
            rows={3}
            value={form.description}
            onChange={set("description")}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="r-url">Link</Label>
            <Input id="r-url" placeholder="https://" value={form.url} onChange={set("url")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="r-location">Geographic Location (City, State, Country)</Label>
            <Input
              id="r-location"
              list="resource-locations"
              placeholder="Optional"
              value={form.location}
              onChange={set("location")}
            />
            <datalist id="resource-locations">
              {vocabulary.locations.map((l) => (
                <option key={l} value={l} />
              ))}
            </datalist>
          </div>
          <div className="space-y-2">
            <Label htmlFor="r-email">Email</Label>
            <Input id="r-email" type="email" value={form.email} onChange={set("email")} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="r-phone">Phone</Label>
            <Input id="r-phone" value={form.phone} onChange={set("phone")} />
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            disabled={!ready || busy}
            onClick={() =>
              onSubmit({
                ...form,
                category: chosenCategory,
                tags,
              })
            }
          >
            {busy && <Spinner />} Submit
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
